import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronRight, FiMessageCircle } from 'react-icons/fi';

import type { Discussion } from '../../../helpers/api/discussions/discussions';
import { resolveRequestIntent } from '../../../helpers/api/discussions/discussions';
import { formatCollaborationRelativeTime, formatDiscussionLocationLabel } from './collaboration-labels';
import { STATUS_BADGE_STYLES } from './collaboration-badges';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatWhen(iso: string): string {
  return formatCollaborationRelativeTime(iso);
}

function StatusBadge({ status }: { status?: string | null }) {
  const { t } = useTranslation('ClinicalCollaboration');
  if (!status) return null;

  const styles = STATUS_BADGE_STYLES;

  const key = `status.${status.toLowerCase()}` as const;

  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        styles[status] ?? styles.CLOSED
      }`}
    >
      {t(key, { defaultValue: status })}
    </span>
  );
}

type ConversationCardProps = {
  discussion: Discussion;
  onClick: () => void;
};

export default function ConversationCard({ discussion, onClick }: ConversationCardProps) {
  const { t } = useTranslation('ClinicalCollaboration');
  const isClosed = discussion.status === 'CLOSED';
  const lastActivity = discussion.last_reply_at ?? discussion.created_at;
  const intent = resolveRequestIntent(discussion);
  const isReview = intent === 'REVIEW';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full gap-3 rounded-xl border p-3 text-left transition-all hover:shadow-sm ${
        isClosed
          ? 'border-slate-100 bg-slate-50/80 opacity-75 dark:border-gray-800 dark:bg-gray-900/50'
          : discussion.unread_for_current_user
            ? 'border-blue-200 bg-blue-50/40 dark:border-blue-700 dark:bg-blue-900/40'
            : 'border-slate-200 bg-white hover:border-slate-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600'
      }`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
          isReview
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
            : 'bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-gray-300'
        }`}
      >
        {initials(discussion.author.display_name)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
              {discussion.author.display_name}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <span
                className={`text-[10px] font-medium uppercase tracking-wide ${
                  isReview ? 'text-amber-700 dark:text-amber-400' : 'text-slate-400'
                }`}
              >
                {isReview ? t('intent.review') : t('intent.readOnly')}
              </span>
              {isReview && <StatusBadge status={discussion.status} />}
            </div>
          </div>
          <span className="shrink-0 text-[11px] text-slate-400 dark:text-gray-500">
            {formatWhen(lastActivity)}
          </span>
        </div>

        {discussion.target_label && (
          <p className="mt-1.5 truncate text-[11px] font-medium text-blue-600 dark:text-blue-400">
            {formatDiscussionLocationLabel(discussion)}
          </p>
        )}

        <p
          className={`mt-1 line-clamp-2 text-sm leading-snug ${
            isClosed ? 'text-slate-400' : 'text-slate-700 dark:text-gray-200'
          }`}
        >
          {discussion.content}
        </p>

        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400 dark:text-gray-500">
          <FiMessageCircle size={11} />
          {discussion.reply_count > 0 ? (
            <span>
              {t('replyCount', { count: discussion.reply_count })}
              {discussion.last_reply_author && (
                <> · {t('lastBy', { name: discussion.last_reply_author })}</>
              )}
            </span>
          ) : (
            <span>{t('noRepliesYet')}</span>
          )}
          {discussion.unread_for_current_user && !isClosed && (
            <span className="ml-auto flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              {t('unread')}
            </span>
          )}
        </div>
      </div>

      <FiChevronRight
        className="mt-3 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500 dark:text-gray-600 dark:group-hover:text-gray-400"
        size={16}
      />
    </button>
  );
}

export { StatusBadge };
